import React, { useState, useEffect } from 'react';
import { X, FileText, Check, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { getInstitutionalDocument, getInstitutionalDocumentPreviewUrl, type InstitutionalDocumentVersion } from '@/services/validationService';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: 'REGLEMENT_INTERIEUR_ETUDIANTS' | 'REGLEMENT_INTERIEUR_STAFF' | 'CONSENTEMENT_MEDIAS';
  isOptional?: boolean;
  onSign: (versionId: string) => void;
  onSkip?: () => void;
}

const DOCUMENT_LABELS: Record<string, string> = {
  REGLEMENT_INTERIEUR_ETUDIANTS: 'Règlement Intérieur - Étudiants',
  REGLEMENT_INTERIEUR_STAFF: 'Règlement Intérieur - Personnel',
  CONSENTEMENT_MEDIAS: 'Consentement pour les Médias et Réseaux Sociaux',
};

export const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onClose,
  documentType,
  isOptional = false,
  onSign,
  onSkip,
}) => {
  const [documentInfo, setDocumentInfo] = useState<InstitutionalDocumentVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasRead, setHasRead] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  // The PDF lives on the API origin (api.nexedu.ma); framing it cross-origin is
  // blocked by X-Frame-Options ("refused to connect"). Fetch it as a blob and
  // render from a same-origin blob URL, which framing rules don't restrict.
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDocument();
    }
  }, [isOpen, documentType]);

  useEffect(() => {
    if (!isOpen || !documentInfo) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfBlobUrl(null);
    setPdfError(false);
    const url = getInstitutionalDocumentPreviewUrl(documentType, documentInfo.version.id);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPdfError(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, documentInfo, documentType]);

  const loadDocument = async () => {
    setIsLoading(true);
    setError(null);
    setHasRead(false);

    try {
      const doc = await getInstitutionalDocument(documentType);
      if (doc) {
        setDocumentInfo(doc);
      } else {
        setError(`Le document "${DOCUMENT_LABELS[documentType]}" n'est pas encore disponible. Veuillez contacter l'administration.`);
      }
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Erreur lors du chargement du document');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSign = async () => {
    if (!documentInfo || !hasRead) return;

    setIsSigning(true);
    try {
      await onSign(documentInfo.version.id);
    } finally {
      setIsSigning(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  if (!isOpen) return null;

  const previewUrl = getInstitutionalDocumentPreviewUrl(documentType, documentInfo?.version.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full mx-4 h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {DOCUMENT_LABELS[documentType]}
              </h2>
              {isOptional && (
                <span className="text-sm text-gray-500">(Optionnel)</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Document preview takes full available space */}
        <div className="flex-1 min-h-0 flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              <span className="ml-3 text-gray-600">Chargement du document...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
              <p className="text-gray-700">{error}</p>
              {isOptional && (
                <button
                  onClick={handleSkip}
                  className="mt-4 px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Passer cette étape
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Document info bar */}
              <div className="flex-shrink-0 px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {documentInfo?.version.originalFileName}
                  </span>
                  <span className="text-xs text-gray-400">
                    (Version {documentInfo?.version.versionNumber})
                  </span>
                </div>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-1 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  Nouvel onglet
                </a>
              </div>

              {/* Document preview - takes all remaining space */}
              <div className="flex-1 min-h-0">
                {pdfError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center text-gray-500 p-6">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                    <p className="text-sm">Impossible d'afficher le document ici.</p>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ouvrir dans un nouvel onglet
                    </a>
                  </div>
                ) : pdfBlobUrl ? (
                  <iframe
                    src={pdfBlobUrl}
                    className="w-full h-full border-0"
                    title={DOCUMENT_LABELS[documentType]}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Checkbox section - Always visible, outside scroll area */}
        {!isLoading && !error && (
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-white">
            <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg border-2 border-gray-200 hover:border-primary-300 transition-colors">
              <input
                type="checkbox"
                checked={hasRead}
                onChange={(e) => setHasRead(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">
                {documentType === 'CONSENTEMENT_MEDIAS' ? (
                  <>
                    J'ai lu et je <strong>consens</strong> à l'utilisation de mon image et de mes informations
                    sur les médias et réseaux sociaux de l'établissement conformément aux termes décrits ci-dessus.
                  </>
                ) : (
                  <>
                    J'ai lu et je <strong>m'engage à respecter</strong> le règlement intérieur de l'établissement.
                    Je comprends que tout manquement pourra entraîner des sanctions disciplinaires.
                  </>
                )}
              </span>
            </label>
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {isOptional && !error && (
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Ne pas accepter
            </button>
          )}

          {!isOptional && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
          )}

          {!error && (
            <button
              onClick={handleSign}
              disabled={!hasRead || isSigning}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {documentType === 'CONSENTEMENT_MEDIAS' ? 'J\'accepte' : 'Je signe'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
